/**
 * 商品詳細。AIO の面（語彙・答え・配信状態）までを出す。
 *
 * 数えていないもの（DataGap）は 0 件と混ぜず、畳んだ注記として置く。
 */

import { useCallback } from "react";
import { Link, useLocation, useParams } from "react-router";
import { useQuery } from "urql";
import {
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  LoadingState,
  Meter,
  PageHeader,
  ProductDocument,
  ProductImage,
  Stat,
  Tag,
  type EvidenceType,
  type ProductQuery,
  type PublishState,
  type ValueSource,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";
import { formatJstWithZone } from "../format-time";

import {
  isModelGenerated,
  isStubGenerated,
  MODEL_FAQ_NOTE,
  STUB_FAQ_NOTE,
} from "../api-capabilities";
import { FaqDuplicatePanel } from "./FaqDuplicatePanel";
import { FaqGenerationPanel } from "./FaqGenerationPanel";
import { PageGenerationPanel } from "./PageGenerationPanel";

const PUBLISH_LABEL: Record<PublishState, string> = {
  PUBLISHED: "配信中",
  STOPPED: "停止中",
  UNSET: "未設定",
};

const SOURCE_LABEL: Record<ValueSource, string> = {
  MASTER: "商品マスター",
  REVIEW: "レビュー",
  UGC: "UGC",
  VIDEO: "動画",
  INFERRED: "推定",
  MANUAL: "手入力",
  NONE: "根拠なし",
};

const EVIDENCE_LABEL: Record<EvidenceType, string> = {
  MASTER: "商品マスター",
  REVIEW_AGG: "レビューの集計",
  REVIEW_PROFILE_AGG: "レビュアー属性の集計",
  REVIEW_TEXT: "レビュー本文",
  UGC: "UGC",
  INFERRED: "推定",
};

type Answerability = NonNullable<
  NonNullable<ProductQuery["product"]>["answerability"]
>;

/**
 * 答えられる問い。**記録が無ければ「数えていません」と理由**を出す
 * （0 件とは書かない）。数は問いの数（ペルソナの観点 × この商品）である。
 */
function AnswerabilitySection({
  answerability,
}: {
  answerability: Answerability | null;
}) {
  if (answerability === null) {
    return (
      <EmptyState
        title="数えていません"
        description="ペルソナ別の充足の集計をまだ動かしていないか、この商品が集計の対象（売り方の提案の候補を優先し、残りを母数の商品名順で埋めた上限 1,000 件）に入っていません。0 件という意味ではありません。"
      />
    );
  }
  return (
    <>
      <div className="vd-grid">
        <Stat
          label="答えられる問い"
          value={count(answerability.covered)}
          note={`判定できた問い ${count(answerability.decided)} 件のうち`}
        />
        <Stat
          label="FAQ で答えられた問い"
          value={count(answerability.faqCovered)}
          note="生成済みの FAQ で答えられると判定したもの"
        />
      </div>
      {answerability.byPersona.length === 0 ? null : (
        <div className="vd-table-scroll" style={{ marginTop: 12 }}>
          <table className="vd-table">
            <thead>
              <tr>
                <th>ペルソナ</th>
                <th className="vd-num">答えられる問い</th>
                <th className="vd-num">判定できた問い</th>
                <th className="vd-num">FAQ で答えられた問い</th>
              </tr>
            </thead>
            <tbody>
              {answerability.byPersona.map((persona) => (
                <tr key={persona.personaId}>
                  <td>
                    {persona.personaName ?? "（集計のあとで消えたペルソナ）"}
                  </td>
                  <td className="vd-num">{count(persona.covered)}</td>
                  <td className="vd-num">{count(persona.decided)}</td>
                  <td className="vd-num">{count(persona.faqCovered)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="vd-stat-note" style={{ marginBottom: 0 }}>
        集計日時: {formatJstWithZone(answerability.computedAt)}
      </p>
    </>
  );
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

/** 値のあるものだけを並べる。null の項目は書かない */
function subtitle(values: (string | null | undefined)[]): string | undefined {
  const present = values.filter(
    (value): value is string => value != null && value.trim() !== "",
  );
  return present.length === 0 ? undefined : present.join(" ・ ");
}

export function ProductDetailPage() {
  const { productId } = useParams();
  const { hash } = useLocation();
  // 商品を非同期で読み込んだ後、区画が DOM に現れた時点で移動する。
  const scrollToProductPage = useCallback(
    (node: HTMLElement | null) => {
      if (node !== null && hash === "#product-page")
        node.scrollIntoView({ block: "start" });
    },
    [hash],
  );
  const [result, refetch] = useQuery({
    query: ProductDocument,
    variables: { id: productId ?? "" },
    pause: productId === undefined,
  });

  // 生成が終わったら引き直す。作られた答えがこの面に出るのは、この再取得のあと
  const reloadProduct = useCallback(() => {
    refetch({ requestPolicy: "network-only" });
  }, [refetch]);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined) {
    return (
      <ErrorState
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }
  const product = result.data?.product ?? null;
  if (product === null) {
    return (
      <div className="vd-page">
        <EmptyState
          title="この商品は見つかりませんでした"
          description={<Link to="/products">商品一覧へ戻る</Link>}
        />
      </div>
    );
  }

  return (
    <div className="vd-page">
      <PageHeader
        title={product.name ?? "(名前が入っていません)"}
        // sku / brand / category / audience は、データ源に列が無くて null の
        // ことがある。無いものは項目ごと出さない（「—」を並べると、
        // 値が空なのか列が無いのかが混ざる）
        description={subtitle([
          product.sku,
          product.brand,
          product.category,
          product.audience,
        ])}
        titleExtra={<HelpIcon slug="products/detail" />}
        extra={
          <span className="vd-row">
            <Tag>{PUBLISH_LABEL[product.aio.publish]}</Tag>
          </span>
        }
      />

      <div className="vd-stack">
        <Card
          title="この商品の状態"
          note="ここに出している数字は、この商品 1 件についてのものです。"
        >
          {/* 画像は数字の左に 1 枚（デモの見せ方）。取り込めていない・
              読み込めなかったときは、その理由を書いた枠になる */}
          <div
            className="vd-row"
            style={{ alignItems: "flex-start", gap: 16, marginBottom: 12 }}
          >
            <div style={{ width: 200, flexShrink: 0 }}>
              <ProductImage
                imageUrl={product.imageUrl ?? null}
                name={product.name ?? null}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="vd-grid">
                <Stat
                  label="語彙の充足"
                  value={
                    product.aio.coverage == null
                      ? "—"
                      : `${Math.round(product.aio.coverage * 100)} %`
                  }
                  note={`${product.aio.axesFilled} / ${product.aio.axesTotal} 軸`}
                />
                <Stat
                  label="作れている答え"
                  value={count(product.aio.faqTotal)}
                  note="生成済みの答え"
                />
                <Stat
                  label="配信している答え"
                  value={count(product.aio.faqDelivered)}
                  note="配信中のルールが対象にしているもの"
                />
                <Stat
                  label="根拠になるデータ"
                  value={count(product.dataCounts.reviews)}
                  note={`レビュー / UGC ${count(product.dataCounts.socialPosts)} 件`}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Meter ratio={product.aio.coverage ?? null} />
              </div>
            </div>
          </div>
          {product.dataGaps.length === 0 ? null : (
            <div style={{ marginTop: 12 }}>
              <Disclosure
                summary={`数えていないものが ${product.dataGaps.length} 件あります`}
              >
                {product.dataGaps.map((gap) => (
                  <p key={gap.what} style={{ margin: "0 0 4px" }}>
                    <strong>{gap.what}</strong>: {gap.why}（必要なもの:{" "}
                    {gap.needs}）
                  </p>
                ))}
              </Disclosure>
            </div>
          )}
        </Card>

        <Card
          title="答えられる問い"
          note="ペルソナ別の充足の集計で、この商品について判定した問いの数です（配信対象の商品の母数）。"
          extra={<HelpIcon slug="products/detail" anchor="答えられる問い" />}
        >
          <AnswerabilitySection answerability={product.answerability ?? null} />
        </Card>

        <Card title="商品の説明">
          {product.description === null ? (
            <EmptyState
              title="説明文がまだありません"
              description="説明文が無いと、語彙の軸がいくつか空のままになります。"
            />
          ) : (
            <>
              <p style={{ marginTop: 0 }}>{product.description}</p>
              <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                出どころ:{" "}
                {product.descriptionSources.length === 0
                  ? "記録がありません"
                  : product.descriptionSources.join(" / ")}
              </p>
            </>
          )}
        </Card>

        <Card
          title="理想として持ちたい文脈"
          note="どのデータ源からも導けないので、人が登録するものです。"
        >
          {product.idealContext === null ? (
            <EmptyState title="まだ登録されていません" />
          ) : (
            <div className="vd-table-scroll">
              <table className="vd-table">
                <tbody>
                  <tr>
                    <th>読み手</th>
                    <td>{product.idealContext.audience}</td>
                  </tr>
                  <tr>
                    <th>ためらい</th>
                    <td>{product.idealContext.concerns}</td>
                  </tr>
                  <tr>
                    <th>使う場面</th>
                    <td>{product.idealContext.scenes}</td>
                  </tr>
                  <tr>
                    <th>価値</th>
                    <td>{product.idealContext.value}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/*
          商品知識シートへの導線。**一覧では `Product.knowledge` を引かない**
          （商品 1 件につき Databricks 1 本 + DynamoDB 1 本の往復になる）ので、
          充足の数字をここに出さず、開く口だけを置く。
        */}
        <Card
          title="商品知識シート"
          note="属性の値と出所、人手で確定した版はシートで見ます。値を人手で確定・修正するのもこの画面です。"
        >
          <p style={{ margin: 0 }}>
            <Link to={`/knowledge/products/${product.id}`}>
              この商品の知識シートを開く
            </Link>
          </p>
        </Card>

        <Card title="語彙タグ">
          {product.aio.tags.length === 0 ? (
            <EmptyState title="語彙がまだ埋まっていません" />
          ) : (
            <div className="vd-table-scroll">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>軸</th>
                    <th>値</th>
                    <th>出どころ</th>
                  </tr>
                </thead>
                <tbody>
                  {product.aio.tags.map((tag) => (
                    <tr key={tag.axis}>
                      <td>{tag.label}</td>
                      <td>{tag.values.join(" / ")}</td>
                      <td>{SOURCE_LABEL[tag.source]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <FaqGenerationPanel productId={product.id} onFinished={reloadProduct} />

        <FaqDuplicatePanel productId={product.id} onFinished={reloadProduct} />

        <section
          id="product-page"
          aria-label="商品ページ"
          ref={scrollToProductPage}
        >
          <PageGenerationPanel
            productId={product.id}
            page={product.page ?? null}
            onFinished={reloadProduct}
          />
        </section>

        <Card
          title="作れている答え"
          note="根拠の無い答えは作りません。どの答えにも出どころが付いています。"
        >
          {/* 何が作ったかで断り方を変える。両方あれば両方出す(混ざっていることを隠さない) */}
          {product.aio.faqs.some((faq) => isStubGenerated(faq.generator)) ? (
            <p className="vd-job-stub" style={{ marginTop: 0 }}>
              {STUB_FAQ_NOTE}
            </p>
          ) : null}
          {product.aio.faqs.some((faq) => isModelGenerated(faq.generator)) ? (
            <p className="vd-job-stub" style={{ marginTop: 0 }}>
              {MODEL_FAQ_NOTE}
            </p>
          ) : null}
          {product.aio.faqs.length === 0 ? (
            <EmptyState title="まだ答えを作れていません" />
          ) : (
            <div className="vd-stack">
              {product.aio.faqs.map((faq) => (
                <div
                  key={faq.id}
                  style={{
                    borderTop: "1px solid var(--vd-border)",
                    paddingTop: 12,
                  }}
                >
                  <div className="vd-card-title">
                    {faq.question}
                    {faq.delivered ? (
                      <Tag tone="ok">配信中</Tag>
                    ) : (
                      <Tag>未配信</Tag>
                    )}
                  </div>
                  <p style={{ margin: "4px 0" }}>{faq.answer}</p>
                  <p className="vd-stat-note" style={{ margin: 0 }}>
                    {/*
                      出どころは**保存されている文言を優先**する。生成器が
                      「入力のどの項目に基づくか」を照合した結果がここに入るので、
                      種別から引いた一般名(「商品マスター」)で上書きすると、
                      どの項目を見たのかが消える。空のときだけ種別で補う。
                    */}
                    根拠:{" "}
                    {faq.evidence.label !== null &&
                    faq.evidence.label.trim().length > 0
                      ? faq.evidence.label
                      : EVIDENCE_LABEL[faq.evidence.type]}
                    {faq.evidence.count == null
                      ? ""
                      : `（${faq.evidence.count.toLocaleString("ja-JP")} 件）`}
                    {faq.confidence == null
                      ? ""
                      : ` ・ 確からしさ ${Math.round(faq.confidence * 100)} %`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
